import axios from 'axios';
import { API_URL } from '../config/api';

// Global rooms cache - loaded once, shared across all components
let roomsCache = null;
let roomsCachePromise = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Fetches rooms for a single location
 */
const fetchRoomsForLocation = async (location) => {
  try {
    const response = await axios.get(`${API_URL}/api/get-rooms/${location}`);
    return response.data.map(room => ({
      roomNo: room.RoomNo,
      roomName: `${room.RoomNo} ${room.RoomDescription}`,
      parentRoomNo: room.ParentRoomNo
    }));
  } catch (error) {
    console.error(`Error fetching ${location} rooms:`, error);
    return [];
  }
};

/**
 * Fetches all rooms for all locations in parallel
 */
const fetchAllRoomsData = async () => {
  const locations = ['Pomona', 'Lakewood', 'Israel', 'China'];

  // Fetch all locations in parallel instead of sequentially
  const results = await Promise.all(
    locations.map(location => fetchRoomsForLocation(location))
  );

  const roomsData = {
    Pomona: { rooms: results[0], tasks: {}, workers: {} },
    Lakewood: { rooms: results[1], tasks: {}, workers: {} },
    Israel: { rooms: results[2], tasks: {}, workers: {} },
    China: { rooms: results[3], tasks: {}, workers: {} }
  };

  return roomsData;
};

/**
 * Gets cached rooms data or fetches if not available/expired
 * This is the main export - use this in components
 */
export const getRoomsData = async () => {
  const now = Date.now();

  // Return cached data if valid
  if (roomsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return roomsCache;
  }

  // If already fetching, wait for that promise
  if (roomsCachePromise) {
    return roomsCachePromise;
  }

  // Fetch fresh data
  roomsCachePromise = fetchAllRoomsData();

  try {
    roomsCache = await roomsCachePromise;
    cacheTimestamp = now;
    return roomsCache;
  } finally {
    roomsCachePromise = null;
  }
};

/**
 * Fetches tasks for a specific room (with caching)
 */
export const getRoomTasks = async (location, roomNo) => {
  // Ensure base rooms are loaded
  const rooms = await getRoomsData();

  // Check if already cached
  if (rooms[location]?.tasks?.[roomNo]) {
    return rooms[location].tasks[roomNo];
  }

  // Fetch and cache
  try {
    const response = await axios.get(`${API_URL}/api/get-room-tasks/${roomNo}`);
    if (roomsCache && roomsCache[location]) {
      roomsCache[location].tasks[roomNo] = response.data;
    }
    return response.data;
  } catch (error) {
    console.error(`Error fetching tasks for room ${roomNo}:`, error);
    return [];
  }
};

/**
 * Fetches workers for a specific room (with caching)
 */
export const getRoomWorkers = async (location, roomNo) => {
  // Ensure base rooms are loaded
  const rooms = await getRoomsData();

  // Check if already cached
  if (rooms[location]?.workers?.[roomNo]) {
    return rooms[location].workers[roomNo];
  }

  // Fetch and cache
  try {
    const response = await axios.get(`${API_URL}/api/get-room-workers/${roomNo}`);
    if (roomsCache && roomsCache[location]) {
      roomsCache[location].workers[roomNo] = response.data;
    }
    return response.data;
  } catch (error) {
    console.error(`Error fetching workers for room ${roomNo}:`, error);
    return [];
  }
};

/**
 * Forces a cache refresh (use sparingly)
 */
export const invalidateRoomsCache = () => {
  roomsCache = null;
  cacheTimestamp = null;
  roomsCachePromise = null;
};

/**
 * Preload rooms cache - call this on app startup
 */
export const preloadRoomsCache = () => {
  getRoomsData().catch(err => {
    console.error('Failed to preload rooms cache:', err);
  });
};

export default {
  getRoomsData,
  getRoomTasks,
  getRoomWorkers,
  invalidateRoomsCache,
  preloadRoomsCache
};
